/**
 * Grupo e famílias — layout da referência do PO (teste físico, item 26).
 *
 * Duas abas: `Famílias` mostra um card por família (ponto da cor, avatares,
 * nomes, "N pessoas", Completa/Pendente) que abre a folha da família — onde
 * quem está nela ou quem administra edita e dissolve (item 24); `Todos`
 * mostra a lista inteira com busca — e, para quem tem família, um chip
 * "Convidar" na linha de quem está sem família (PO, 20/09/2026); quem já tem
 * convite pendente para a minha aparece como "Aguardando". Quem não está em
 * nenhuma família aparece **separado, não escondido** — é a informação que
 * explica por que a divisão por família conta essa pessoa como uma unidade.
 *
 * O convite pendente aparece no topo, com o prazo. Ele também vive na Central
 * de Notificações; aqui é onde a pessoa vai quando lembra que alguém a chamou.
 */
import { daysLeftOnInvite, groupByFamily, withoutFamily } from '@nv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { Card, ConfirmSheet, EmptyState, ErrorState, SectionLabel, Segmented, Skeleton } from '../../ui/primitives';
import type { MembroDaViagem } from '../../trip/TripContext';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { useItinerary } from '../roteiro/store';
import { answerInvite, type FamilyView, GrupoError, inviteToFamily, readGrupo } from './api';
import { FamiliaSheet } from './FamiliaSheet';

export function GrupoScreen({
  tripId,
  membros,
  meuMemberId,
  permissoes = [],
}: {
  tripId: string;
  membros: MembroDaViagem[];
  meuMemberId: string;
  permissoes?: string[];
}) {
  const podeGerenciar = permissoes.includes('manage_permissions');
  // Convidar é governança, não ação liberável em contexto: quem não tem a
  // chave não vê o botão (sem 🔒). Quem a ganha por override vê na hora,
  // porque `permissoes` é a lista efetiva.
  const podeConvidar = permissoes.includes('manage_members');
  const souDono =
    membros.find((m) => m.memberId === meuMemberId)?.role === 'owner';
  const minhaFamiliaId = membros.find((m) => m.memberId === meuMemberId)?.familyId;
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [aba, setAba] = useState<'familias' | 'todos'>('familias');
  const [busca, setBusca] = useState('');
  const [aberta, setAberta] = useState<FamilyView | null>(null);
  // Convite pela aba Todos: quem está sendo convidado para a minha família.
  const [convidando, setConvidando] = useState<MembroDaViagem | null>(null);

  const grupo = useQuery({
    queryKey: ['grupo', tripId],
    queryFn: () => readGrupo(tripId),
  });

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
    // A lista de participantes carrega a família — e é ela que habilita a
    // divisão por família na tela de despesa.
    void cliente.invalidateQueries({ queryKey: ['viagem'] });
  };

  const convidar = useMutation({
    mutationFn: (m: MembroDaViagem) => inviteToFamily(tripId, minhaFamiliaId ?? '', m.memberId),
    onSuccess: (_r, m) => {
      invalidar();
      useItinerary.getState().showToast(`Convite enviado para ${m.name.split(/\s+/)[0]}.`, 4000);
      setConvidando(null);
    },
    onError: () => invalidar(),
  });

  const responder = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      answerInvite(tripId, id, accept),
    onSuccess: invalidar,
  });

  if (grupo.isLoading) {
    return (
      <section className="grupo">
        <h1 className="grupo__titulo">Grupo</h1>
        <Skeleton height={62} count={3} />
      </section>
    );
  }

  if (grupo.isError) {
    return (
      <section className="grupo">
        <h1 className="grupo__titulo">Grupo</h1>
        <ErrorState
          title="Não foi possível carregar o grupo"
          description="Verifique a conexão e tente de novo."
          onRetry={() => void grupo.refetch()}
        />
      </section>
    );
  }

  const { families, pendingInvitations } = grupo.data!;
  const porFamilia = groupByFamily(membros);
  const semFamilia = withoutFamily(membros);
  const minhaFamilia = minhaFamiliaId;
  const aMinha = families.find((f) => f.id === minhaFamilia) ?? null;

  const nomeDe = (memberId: string) =>
    membros.find((m) => m.memberId === memberId)?.name ?? 'Participante';

  const filtrados = membros.filter((m) =>
    m.name.toLowerCase().includes(busca.trim().toLowerCase()),
  );

  return (
    <section className="grupo">
      <header className="grupo__header">
        <div>
          <h1 className="grupo__titulo">Grupo</h1>
          <p className="grupo__contagem">
            {membros.length} {membros.length === 1 ? 'participante' : 'participantes'} ·{' '}
            {families.length} {families.length === 1 ? 'família' : 'famílias'}
          </p>
        </div>
        {podeConvidar && (
          <button type="button" className="nv-btn nv-btn--primary nv-btn--pilula grupo__convidar" onClick={() => navegar('/grupo/convites')}>
            <Icon name="pessoas" size={16} />
            Convidar
          </button>
        )}
      </header>

      {pendingInvitations.map((c) => (
        <Card variant="surf" className="grupo__convite" key={c.id}>
          <span className="grupo__ponto" style={{ background: c.familyColor }} aria-hidden="true" />
          <div className="grupo__convite-texto">
            <p className="grupo__convite-titulo">
              Você foi convidado para a {c.familyName}
            </p>
            <p className="grupo__convite-prazo">
              {/* O prazo é o que faz a pessoa responder. */}
              {c.daysLeft === 1
                ? 'Expira amanhã'
                : `Expira em ${daysLeftOnInvite(new Date(c.expiresAt).getTime())} dias`}
            </p>
          </div>
          <div className="grupo__convite-acoes">
            <button
              type="button"
              className="nv-btn nv-btn--primary"
              disabled={responder.isPending}
              onClick={() => responder.mutate({ id: c.id, accept: true })}
            >
              Aceitar
            </button>
            <button
              type="button"
              className="nv-btn nv-btn--chip"
              disabled={responder.isPending}
              onClick={() => responder.mutate({ id: c.id, accept: false })}
            >
              Recusar
            </button>
          </div>
        </Card>
      ))}

      {responder.isError && (
        <p className="auth-error" role="alert">
          {(responder.error as GrupoError).message}
        </p>
      )}

      <Segmented
        value={aba}
        options={[
          { valor: 'familias', rotulo: 'Famílias', testId: 'grupo-aba-familias' },
          { valor: 'todos', rotulo: 'Todos', testId: 'grupo-aba-todos' },
        ]}
        onChange={setAba}
        label="Visão do grupo"
        tom="claro"
      />

      {aba === 'familias' && (
        <>
          {families.length === 0 ? (
            <EmptyState
              ilustracao="grupo"
              title="Nenhuma família ainda"
              description="Famílias agrupam quem viaja junto — e habilitam a divisão de despesa por família."
              action={{
                label: '+ Criar minha família',
                onClick: () => navegar('/grupo/criar-familia'),
              }}
            />
          ) : (
            families.map((f, i) => {
              const doGrupo = porFamilia.find((g) => g.familyId === f.id);
              const pessoas = doGrupo?.members ?? f.memberIds.map((id) => membros.find((m) => m.memberId === id)).filter((m): m is MembroDaViagem => Boolean(m));
              const pendente = f.pendingInvites > 0 || pessoas.length === 0;
              return (
                <button type="button" className="grupo__familia nv-enter" style={{ ['--i' as string]: i }} key={f.id} onClick={() => setAberta(f)} data-testid="grupo-familia" data-estado={pendente ? 'pendente' : 'completa'}>
                  {/* Refino final: até 2 avatares na cor da família; sem
                      ninguém, a ilustração do grupo. O ponto vai para o nome. */}
                  <span className="grupo__familia-avatares" aria-hidden="true">
                    {pessoas.length === 0 ? (
                      <Ilustracao nome="grupo" tamanho={48} />
                    ) : (
                      pessoas.slice(0, 2).map((m) => (
                        <FotoAvatar key={m.memberId} nome={m.name} url={m.avatarUrl} cor={f.color} tamanho={36} className="grupo__familia-avatar" />
                      ))
                    )}
                  </span>
                  <span className="grupo__familia-texto">
                    <span className="grupo__familia-nome">
                      <span className="grupo__ponto" style={{ background: f.color }} aria-hidden="true" />
                      {f.name}
                    </span>
                    <span className="grupo__familia-membros">
                      {pessoas.length === 0
                        ? 'Nenhum membro ainda'
                        : `${pessoas.map((m) => m.name.split(/\s+/)[0]).join(', ')} · ${pessoas.length === 1 ? '1 pessoa' : `${pessoas.length} pessoas`}`}
                    </span>
                  </span>
                  <span className={pendente ? 'grupo__estado grupo__estado--pendente' : 'grupo__estado'}>{pendente ? 'Pendente' : 'Completa'}</span>
                </button>
              );
            })
          )}

          {semFamilia.length > 0 && families.length > 0 && (
            <>
              <SectionLabel>SEM FAMÍLIA</SectionLabel>
              <Card variant="chip">
                <p className="auth-note">
                  {/* Não é omissão: é o que explica a conta da divisão. */}
                  Na divisão por família, cada uma destas pessoas conta como uma
                  unidade.
                </p>
                <p className="grupo__familia-membros">
                  {semFamilia.map((m) => m.name).join(', ')}
                </p>
              </Card>
            </>
          )}

          {!minhaFamilia && families.length > 0 && (
            // Quando não há família nenhuma, o estado vazio já oferece a ação.
            <button
              type="button"
              className="nv-btn nv-btn--primary nv-btn--block"
              onClick={() => navegar('/grupo/criar-familia')}
            >
              + Criar minha família
            </button>
          )}
        </>
      )}

      {aba === 'todos' && (
        <>
          <label className="fin__busca">
            <Icon name="buscar" size={18} />
            <input
              className="fin__busca-input"
              type="search"
              placeholder="Buscar pessoa"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar pessoa"
            />
          </label>

          <Card variant="surf">
            {filtrados.map((m) => {
              const conteudo = (
                <>
                  <FotoAvatar nome={m.name} url={m.avatarUrl} cor={m.familyColor} tamanho={34} className="grupo__avatar-foto" />
                  <span className="grupo__pessoa-nome">{m.name}</span>
                  {m.role === 'owner' && <span className="grupo__papel">dono</span>}
                  {m.memberId === meuMemberId && <span className="grupo__papel">você</span>}
                </>
              );

              // Convite para a minha família, direto da lista: só para quem
              // está sem família. Quem já tem convite pendente vê "Aguardando".
              const outra = m.memberId !== meuMemberId;
              const aguardando = Boolean(aMinha?.pendingInviteeIds.includes(m.memberId));
              const chip =
                aMinha && outra && !m.familyId ? (
                  aguardando ? (
                    <span className="grupo__papel" data-testid="grupo-pessoa-aguardando">aguardando</span>
                  ) : (
                    <button
                      type="button"
                      className="nv-btn nv-btn--chip"
                      onClick={() => setConvidando(m)}
                      data-testid="grupo-pessoa-convidar"
                    >
                      Convidar
                    </button>
                  )
                ) : null;

              // A linha só é clicável para quem pode gerenciar permissões e
              // sobre outra pessoa: abrir uma tela que recusa tudo seria
              // prometer uma ação inexistente. O chip fica FORA do link — um
              // botão dentro de um `<a>` é inválido e toca os dois. Com chip,
              // o "›" sai: dois sinais de ação na mesma linha confundem, e o
              // toque no nome continua levando às permissões.
              return (
                <div className="grupo__pessoa" key={m.memberId}>
                  {podeGerenciar && outra ? (
                    <Link className="grupo__pessoa-link" to={`/grupo/${m.memberId}/permissoes`}>
                      {conteudo}
                      {!chip && <span className="fin__linha-detalhe" aria-hidden="true">›</span>}
                    </Link>
                  ) : (
                    <span className="grupo__pessoa-link">{conteudo}</span>
                  )}
                  {chip}
                </div>
              );
            })}
            {filtrados.length === 0 && <p className="auth-note">Ninguém com esse nome.</p>}
          </Card>
        </>
      )}

      {convidando && aMinha && (
        <ConfirmSheet
          titulo={`Convidar ${convidando.name.split(/\s+/)[0]} para a ${aMinha.name}?`}
          texto="A pessoa recebe um convite e decide — ninguém entra sem aceitar."
          confirmar="Convidar"
          tom="normal"
          ocupado={convidar.isPending}
          testId="confirmar-convidar"
          onCancel={() => setConvidando(null)}
          onConfirm={() => convidar.mutate(convidando)}
        />
      )}
      {convidar.isError && !convidando && (
        <p className="auth-error" role="alert">{(convidar.error as GrupoError).message}</p>
      )}

      {aberta && (
        <FamiliaSheet
          tripId={tripId}
          familia={families.find((f) => f.id === aberta.id) ?? aberta}
          membros={membros}
          meuMemberId={meuMemberId}
          podeAdministrar={podeConvidar}
          onFechar={() => setAberta(null)}
        />
      )}

      {souDono && (
        <>
          <SectionLabel>ZONA SENSÍVEL</SectionLabel>
          <button
            type="button"
            className="nv-btn nv-btn--ghost-danger nv-btn--block"
            onClick={() => navegar('/grupo/transferir')}
          >
            Transferir propriedade da viagem
          </button>
        </>
      )}
    </section>
  );
}
